import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { PermissionAction } from '@experiences/interfaces';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    useNavigateWithParams,
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApButton,
    ApLink,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWR from 'swr';

import { routePaths } from '../../common/constants/routePaths';
import { useDocumentationLinks } from '../../common/hooks/useDocumentationLink';
import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import {
    getClientConfig,
    getRelayGroupDetails,
} from '../../services/relay/RelayService';
import { accountGlobalId } from '../../store/selectors';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import type { RelayClientConfigDialogProps } from './RelayClientConfigDialog';
import RelayClientConfigDialog from './RelayClientConfigDialog';
import { useHasRelayPermission } from './useRelayPermission';

// Returned by the relay client-config endpoint when the relay group already has the
// maximum number of client secrets.
const RELAY_MAX_CLIENT_SECRETS_ERROR_CODE = 2000;

const useStyles = makeStyles(theme => ({
    headingContainer: { marginBottom: theme.spacing(2) },
    infoBanner: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.info.light,
        borderRadius: theme.shape.borderRadius,
    },
    infoIcon: {
        fontSize: '24px',
        color: theme.palette.info.main,
    },
    instructionHeader: { marginTop: theme.spacing(4) },
    instructionSection: { flex: 1 },
    instructionContent: {
        marginLeft: theme.spacing(2.5),
        marginTop: 1,
    },
    closeButton: { marginTop: theme.spacing(4) },
}));

const RelayClientSetupPageComponent: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const createDialog = useShowDialog();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();
    const { breadcrumbs } = useBreadcrumbs();
    const accountId = useSelector(accountGlobalId);
    const setErrorMessage = useCentralErrorSetter();
    const {
        getErrorMessage, parseErrorObject,
    } = useGetErrorInfo();
    const getLocalizedLinks = useDocumentationLinks();
    const relayDownloadLink = useFeatureFlagValue(Features.RelayDownloadSoftwareLink.name, Features.RelayDownloadSoftwareLink.defaultValue);
    const relayDocsSlug = useFeatureFlagValue(Features.RelayConnectionInstructionsLink.name, Features.RelayConnectionInstructionsLink.defaultValue);

    const {
        tenantId = '', relayGroupId = '', type: typeParam,
    } = useParams<{ tenantId: string; relayGroupId: string; type: 'add' | 'edit' | 'view' }>();
    const initialTenantIdRef = useRef(tenantId);

    const isTenantStale = tenantId !== initialTenantIdRef.current;
    const { data: relayGroupDetails } = useSWR(
        !isTenantStale && accountId && tenantId && relayGroupId
            ? [ 'relay-group-details', accountId, tenantId, relayGroupId ]
            : null,
        () => getRelayGroupDetails(accountId, tenantId, relayGroupId),
    );

    useEffect(() => {
        if (tenantId !== initialTenantIdRef.current) {
            navigate(getRoute(routePaths.adminTenantRelay.replace(':tenantId', tenantId)));
        }
    }, [ tenantId, navigate, getRoute ]);

    const [ isGenerating, setIsGenerating ] = useState(false);
    const hasPermission = useHasRelayPermission([ PermissionAction.Relay.RelayGroupConfig.View ], tenantId);

    const handleClose = useCallback(() => {
        if (tenantId && relayGroupId) {
            navigate(getRoute(
                routePaths.adminTenantRelayDetails
                    .replace(':tenantId', tenantId)
                    .replace(':relayGroupId', relayGroupId)
            ));
        }
    }, [ navigate, getRoute, tenantId, relayGroupId ]);

    const handleGenerateConfig = useCallback(async () => {
        if (!accountId || !tenantId || !relayGroupId || isGenerating) {
            return;
        }

        setIsGenerating(true);
        try {
            const response = await getClientConfig(accountId, tenantId, relayGroupId);
            const dialogContentProps: RelayClientConfigDialogProps = { configContents: response.config };
            createDialog({
                title: translate({ id: 'CLIENT_RELAY_CLIENT_CONFIG' }),
                customDialogContent: RelayClientConfigDialog,
                customDialogContentProps: dialogContentProps,
                width: '600px',
                dataCy: 'client-config-dialog',
            });
        } catch (error) {
            const errorObject = await parseErrorObject(error);
            if (errorObject?.errorCode === RELAY_MAX_CLIENT_SECRETS_ERROR_CODE) {
                setErrorMessage(translate({ id: 'CLIENT_RELAY_MAX_CLIENT_SECRETS_ERROR' }));
            } else {
                setErrorMessage(await getErrorMessage(error));
            }
        } finally {
            setIsGenerating(false);
        }
    }, [ accountId, tenantId, relayGroupId, createDialog, translate, getErrorMessage, parseErrorObject, setErrorMessage, isGenerating ]);

    const fullBreadcrumbs = useMemo(() => [
        ...breadcrumbs,
        {
            index: 3,
            name: relayGroupDetails?.name ?? '',
            link: routePaths.adminTenantRelayDetails.replace(':tenantId', tenantId ?? '').replace(':relayGroupId', relayGroupId ?? ''),
        },
        {
            index: 4,
            name: translate({ id: 'CLIENT_VIEW_SETUP_INSTRUCTIONS' }),
            link: routePaths.adminTenantRelaySetupInstructions,
        },
    ], [ breadcrumbs, relayGroupDetails, tenantId, relayGroupId, translate ]);

    return (
        <UiPageContainer
            maxWidth='800px'
            position='left'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={fullBreadcrumbs} />}>
            {(typeParam === 'add' || typeParam === 'edit') && (
                <Box className={classes.headingContainer}>
                    <UiText variant={FontVariantToken.fontSizeH4Bold}>
                        {translate({
                            id: typeParam === 'add'
                                ? 'CLIENT_ALMOST_FINISHED'
                                : 'CLIENT_RELAY_GROUP_EDITED_SUCCESSFULLY',
                        })}
                    </UiText>
                </Box>
            )}
            <UiStack
                gap={SpacingToken.XS}
                align='center'
                className={classes.infoBanner}>
                <InfoOutlinedIcon className={classes.infoIcon} />
                <UiText variant={FontVariantToken.fontSizeM}>
                    {translate({ id: 'CLIENT_RELAY_SETUP_INSTRUCTIONS_INFO' })}
                </UiText>
            </UiStack>

            <UiStack
                direction="column"
                gap={SpacingToken.XXL}
                align="start">
                <Box className={classes.instructionSection}>
                    <Box className={classes.instructionHeader}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: 'CLIENT_RELAY_DOWNLOAD_STEP_TITLE' })}
                        </UiText>
                        <Box className={classes.instructionContent}>
                            <UiText variant={FontVariantToken.fontSizeM}>
                                <FormattedMessage
                                    id="CLIENT_RELAY_DOWNLOAD_SOFTWARE_FROM"
                                    values={{
                                        link: (chunks: React.ReactNode[]) => (
                                            <ApLink
                                                href={relayDownloadLink as string}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {chunks}
                                            </ApLink>
                                        ),
                                    }}
                                />
                            </UiText>
                        </Box>
                    </Box>
                    <Box className={classes.instructionHeader}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: 'CLIENT_RELAY_CONFIG_GENERATE_STEP_TITLE' })}
                        </UiText>
                        <Box className={classes.instructionContent}>
                            <UiText variant={FontVariantToken.fontSizeM}>
                                {translate({ id: 'CLIENT_RELAY_CONFIG_GENERATE_INSTRUCTIONS' })}
                            </UiText>
                            <Box sx={{ marginTop: 1 }}>
                                <ApButton
                                    label={translate({ id: 'CLIENT_GENERATE_CONFIG' })}
                                    variant="secondary"
                                    onClick={handleGenerateConfig}
                                    loading={isGenerating}
                                    disabled={isGenerating || !hasPermission}
                                    data-cy="generate-config-button"
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.instructionHeader}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: 'CLIENT_RELAY_INSTALL_STEP_TITLE' })}
                        </UiText>
                        <Box className={classes.instructionContent}>
                            <UiText variant={FontVariantToken.fontSizeM}>
                                {translate(
                                    { id: 'CLIENT_RELAY_FOLLOW_CONNECTION_INSTRUCTIONS' },
                                    {
                                        link: (
                                            <ApLink
                                                target="_blank"
                                                href={getLocalizedLinks({ articleSlug: relayDocsSlug as string })}>
                                                {translate({ id: 'CLIENT_RELAY_DOCS' })}
                                            </ApLink>
                                        ),
                                    })}
                            </UiText>
                        </Box>
                    </Box>
                </Box>
            </UiStack>

            <UiStack
                justify="end"
                className={classes.closeButton}>
                <ApButton
                    label={translate({ id: 'CLIENT_CLOSE' })}
                    variant="primary"
                    onClick={handleClose}
                    data-cy="close-button"
                />
            </UiStack>
        </UiPageContainer>
    );
};

export default RelayClientSetupPageComponent;
